import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString();

const PRESETS = [
  { name: "Promotions older than 30 days", category: "promotions", days: 30 },
  { name: "Newsletters older than 30 days", category: "newsletters", days: 30 },
  { name: "Social older than 14 days", category: "social", days: 14 },
  { name: "Receipts older than 1 year", category: "receipts", days: 365 },
];

function Section({ title, right, children, testid }) {
  return (
    <section className="border border-border bg-background min-w-0" data-testid={testid}>
      <header className="flex items-center justify-between px-3 py-2 border-b border-border">
        <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground">{title}</h2>
        {right}
      </header>
      {children}
    </section>
  );
}

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

// Suggestions, saved filters, scheduled rules and reclaim analytics for one account.
export default function CleanupExtras({ accountId, sel, hasSelection, onApply, refreshKey }) {
  const [suggestions, setSuggestions] = useState([]);
  const [saved, setSaved] = useState([]);
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [name, setName] = useState("");
  const [everyHours, setEveryHours] = useState(24);

  const load = useCallback(async () => {
    if (!accountId) return;
    const [s, f, r, a] = await Promise.allSettled([
      api.get(`/cleanup/accounts/${accountId}/suggestions`),
      api.get("/cleanup/saved-filters"),
      api.get("/cleanup/rules", { params: { account_id: accountId } }),
      api.get("/cleanup/analytics", { params: { account_id: accountId, days: 30 } }),
    ]);
    setSuggestions(s.status === "fulfilled" ? s.value.data.items : []);
    setSaved(f.status === "fulfilled" ? f.value.data.items : []);
    setRules(r.status === "fulfilled" ? r.value.data.items : []);
    setStats(a.status === "fulfilled" ? a.value.data : null);
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const saveFilter = async () => {
    try {
      await api.post("/cleanup/saved-filters", { name: name.trim(), filter: sel });
      setName("");
      toast.success("Filter saved");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not save filter"));
    }
  };

  const createRule = async () => {
    try {
      await api.post("/cleanup/rules", {
        name: name.trim(),
        account_id: accountId,
        filter: sel,
        every_hours: Number(everyHours) || 24,
      });
      setName("");
      toast.success("Rule created");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not create rule"));
    }
  };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    }
  };

  const canSave = hasSelection && name.trim().length > 0;
  const toSel = (f) => f;

  const hasRule = (n) => rules.some((r) => r.name === n);
  const addPreset = (p) =>
    act(
      () =>
        api.post("/cleanup/rules", {
          name: p.name,
          account_id: accountId,
          filter: { categories: [p.category], older_than_days: p.days },
          every_hours: 24,
        }),
      `Auto-clean on: ${p.name}`
    );

  return (
    <div className="grid gap-4 lg:grid-cols-2 mb-4" data-testid="cleanup-extras">
      <Section title="Suggestions" testid="suggestions-panel">
        {suggestions.length === 0 && <p className="p-3 text-sm text-muted-foreground">Nothing to suggest yet.</p>}
        {suggestions.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onApply(toSel(s.filter))}
            className="w-full text-left px-3 py-2 border-b border-border hover:bg-secondary/40 text-sm"
            data-testid={`suggestion-${s.id}`}
          >
            {s.title}
          </button>
        ))}
      </Section>

      <Section
        title="Reclaimed & time saved (30 days)"
        testid="analytics-panel"
      >
        {stats ? (
          <div className="p-3 text-sm font-mono space-y-2">
            <div>
              <b>{fmt(stats.deleted)}</b> deleted · <b>{stats.reclaimed_mb}</b> MB reclaimed ·{" "}
              <b data-testid="analytics-time-saved">{fmt(stats.time_saved_minutes)}</b> min saved
            </div>
            <div className="flex items-end gap-px h-12" aria-label="Deletions per day">
              {stats.per_day.map((d) => {
                const max = Math.max(...stats.per_day.map((x) => x.count), 1);
                return (
                  <div
                    key={d.date}
                    title={`${d.date}: ${fmt(d.count)} deleted, ${fmt(d.time_saved_minutes)} min saved`}
                    className="flex-1 bg-primary/60"
                    style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }}
                  />
                );
              })}
            </div>
            {stats.top_deleted_senders.length > 0 && (
              <div className="text-xs text-muted-foreground truncate">
                Top: {stats.top_deleted_senders.map((t) => `${t.sender} (${fmt(t.count)})`).join(", ")}
              </div>
            )}
          </div>
        ) : (
          <p className="p-3 text-sm text-muted-foreground">No data yet.</p>
        )}
      </Section>

      <Section
        title="Saved filters & rules"
        testid="filters-panel"
        right={
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              maxLength={80}
              className="h-7 w-28 border border-border bg-background px-2 text-xs font-mono"
              data-testid="filter-name"
            />
            <Button size="sm" variant="outline" disabled={!canSave} onClick={saveFilter} data-testid="save-filter">
              Save
            </Button>
            <input
              type="number"
              min={1}
              value={everyHours}
              onChange={(e) => setEveryHours(e.target.value)}
              className="h-7 w-14 border border-border bg-background px-2 text-xs font-mono"
              title="Run every N hours"
              data-testid="rule-hours"
            />
            <Button size="sm" variant="outline" disabled={!canSave} onClick={createRule} data-testid="create-rule">
              Auto-run
            </Button>
          </div>
        }
      >
        <div className="flex flex-wrap gap-2 p-3 border-b border-border" data-testid="presets">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              type="button"
              disabled={hasRule(p.name)}
              onClick={() => addPreset(p)}
              className="h-7 px-2 text-xs font-mono border border-border hover:bg-secondary/40 disabled:opacity-50"
              data-testid={`preset-${p.category}`}
            >
              {hasRule(p.name) ? "✓ " : "+ "}
              Auto-clear {p.name.toLowerCase()}
            </button>
          ))}
        </div>
        {saved.length === 0 && rules.length === 0 && (
          <p className="p-3 text-sm text-muted-foreground">
            Select buckets, name them, then save as a filter or schedule as a rule.
          </p>
        )}
        {saved.map((f) => (
          <div key={f.filter_id} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border text-sm">
            <button type="button" className="truncate hover:underline" onClick={() => onApply(toSel(f.filter))} data-testid={`saved-${f.filter_id}`}>
              {f.name}
            </button>
            <button
              type="button"
              className="text-xs underline text-muted-foreground"
              onClick={() => act(() => api.delete(`/cleanup/saved-filters/${f.filter_id}`))}
            >
              Delete
            </button>
          </div>
        ))}
        {rules.map((r) => (
          <div key={r.rule_id} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border text-sm" data-testid={`rule-${r.rule_id}`}>
            <span className="truncate">
              {r.name} <span className="text-xs text-muted-foreground font-mono">every {r.every_hours}h · {r.enabled ? "on" : "off"}</span>
            </span>
            <span className="flex gap-3 text-xs shrink-0">
              <button type="button" className="underline" onClick={() => act(() => api.post(`/cleanup/rules/${r.rule_id}/run-now`), "Rule queued")}>
                Run
              </button>
              <button type="button" className="underline" onClick={() => act(() => api.post(`/cleanup/rules/${r.rule_id}/toggle`))}>
                {r.enabled ? "Pause" : "Enable"}
              </button>
              <button type="button" className="underline text-destructive" onClick={() => act(() => api.delete(`/cleanup/rules/${r.rule_id}`))}>
                Delete
              </button>
            </span>
          </div>
        ))}
      </Section>
    </div>
  );
}
